import type { ReactNode } from 'react'

export type StatusTone = 'positive' | 'info' | 'caution' | 'critical' | 'neutral' | 'example'

/**
 * `mark` carries the status without relying on colour, so it stays on by
 * default. The example tone turns it off: "Example" is a category label rather
 * than a status, and a glyph in front of it reads as a state it does not have.
 */
export function Badge({ children, tone = 'neutral', mark = tone !== 'example' }: { children: ReactNode; tone?: StatusTone; mark?: boolean }) {
  return <span className={`ui-badge ui-badge-${tone}`}>{mark && <span aria-hidden="true" className="ui-badge-mark">{tone === 'positive' ? '✓' : tone === 'critical' ? '×' : tone === 'caution' ? '!' : tone === 'info' ? '·' : '—'}</span>}{children}</span>
}

/**
 * The read-only marker for sample content.
 *
 * Shared rather than route-local so the workspace and the public product
 * preview mark example data identically, and so neither has to invent its own
 * wording or colour for it. The word is part of the accessible name, not only
 * the styling, because "this is not your data" must survive a screen reader.
 */
export function ExampleBadge({ label = 'Example' }: { label?: string }) {
  return <Badge tone="example">{label}</Badge>
}

export function StatusPill({ status, label }: { status: StatusTone; label: string }) {
  return <Badge tone={status}>{label}</Badge>
}
